type CognitoConfig = {
  readonly cognitoEndpoint: string;
  readonly clientId: string;
};

type AuthResult = {
  readonly AccessToken: string;
  readonly IdToken: string;
  readonly RefreshToken?: string;
};

type CognitoAuthResponse = {
  readonly AuthenticationResult?: AuthResult;
  readonly ChallengeName?: string;
};

const cognitoCall = async <T>(endpoint: string, action: string, params: Record<string, unknown>): Promise<T> => {
  const resp = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${action}`,
    },
    body: JSON.stringify(params),
  });
  const data = await resp.json() as T & { message?: string; __type?: string };
  if (!resp.ok) {
    const msg = data.message ?? data.__type ?? 'Unknown error';
    throw new Error(msg);
  }
  return data;
};

const signIn = async (config: CognitoConfig, email: string, password: string): Promise<CognitoAuthResponse> =>
  cognitoCall<CognitoAuthResponse>(config.cognitoEndpoint, 'InitiateAuth', {
    AuthFlow: 'USER_PASSWORD_AUTH',
    ClientId: config.clientId,
    AuthParameters: { USERNAME: email, PASSWORD: password },
  });

const signUp = async (config: CognitoConfig, email: string, password: string, tosVersion: string): Promise<void> => {
  await cognitoCall<Record<string, unknown>>(config.cognitoEndpoint, 'SignUp', {
    ClientId: config.clientId,
    Username: email,
    Password: password,
    UserAttributes: [
      { Name: 'email', Value: email },
      { Name: 'custom:tos_accepted_at', Value: new Date().toISOString() },
      { Name: 'custom:tos_version', Value: tosVersion },
    ],
  });
};

const confirmSignUp = async (config: CognitoConfig, email: string, code: string): Promise<void> => {
  await cognitoCall<Record<string, unknown>>(config.cognitoEndpoint, 'ConfirmSignUp', {
    ClientId: config.clientId,
    Username: email,
    ConfirmationCode: code,
  });
};

const resendConfirmationCode = async (config: CognitoConfig, email: string): Promise<void> => {
  await cognitoCall<Record<string, unknown>>(config.cognitoEndpoint, 'ResendConfirmationCode', {
    ClientId: config.clientId,
    Username: email,
  });
};

const forgotPassword = async (config: CognitoConfig, email: string): Promise<void> => {
  await cognitoCall<Record<string, unknown>>(config.cognitoEndpoint, 'ForgotPassword', {
    ClientId: config.clientId,
    Username: email,
  });
};

const resetPassword = async (config: CognitoConfig, email: string, code: string, newPassword: string): Promise<void> => {
  await cognitoCall<Record<string, unknown>>(config.cognitoEndpoint, 'ConfirmForgotPassword', {
    ClientId: config.clientId,
    Username: email,
    ConfirmationCode: code,
    Password: newPassword,
  });
};

export { cognitoCall, signIn, signUp, confirmSignUp, resendConfirmationCode, forgotPassword, resetPassword };
export type { CognitoConfig, AuthResult, CognitoAuthResponse };
